/**
 * OfflineStatusBar – dezente Anzeige des Sync-Status + Konflikthinweise
 * Zeigt: Offline | Wird synchronisiert... | Synchronisiert | X ausstehend | Konflikte
 */
import { useOffline } from "../hooks/useOffline";
import { Wifi, WifiOff, RefreshCw, Check, CloudUpload, AlertTriangle, X } from "lucide-react";

export default function OfflineStatusBar() {
  const { isOnline, syncStatus, totalPending, triggerSync, conflicts, dismissAllConflicts } = useOffline();

  const hasConflicts = conflicts.length > 0;

  // Nichts zeigen wenn online + keine ausstehenden + idle + keine Konflikte
  if (isOnline && totalPending === 0 && syncStatus === "idle" && !hasConflicts) return null;

  let bg, icon, text;

  if (!isOnline) {
    bg = "bg-amber-500/90";
    icon = <WifiOff className="w-3.5 h-3.5" />;
    text = "Offline - Daten werden lokal gespeichert";
  } else if (syncStatus === "syncing") {
    bg = "bg-blue-500/90";
    icon = <RefreshCw className="w-3.5 h-3.5 animate-spin" />;
    text = "Wird synchronisiert...";
  } else if (hasConflicts) {
    bg = "bg-red-500/90";
    icon = <AlertTriangle className="w-3.5 h-3.5" />;
    text = `${conflicts.length} Konflikt${conflicts.length > 1 ? "e" : ""} bei der Synchronisierung`;
  } else if (syncStatus === "done") {
    bg = "bg-green-500/90";
    icon = <Check className="w-3.5 h-3.5" />;
    text = "Synchronisiert";
  } else if (totalPending > 0) {
    bg = "bg-blue-500/80";
    icon = <CloudUpload className="w-3.5 h-3.5" />;
    text = `${totalPending} ausstehend`;
  } else {
    return null;
  }

  return (
    <div data-testid="offline-status-bar">
      <div
        className={`${bg} text-white px-3 py-1.5 flex items-center justify-center gap-2 text-xs font-medium z-50`}
        onClick={isOnline && totalPending > 0 ? triggerSync : undefined}
        role={isOnline && totalPending > 0 ? "button" : undefined}
      >
        {icon}
        <span>{text}</span>
        {isOnline && totalPending > 0 && (
          <button
            onClick={(e) => { e.stopPropagation(); triggerSync(); }}
            className="ml-1 underline text-white/80 hover:text-white"
            data-testid="manual-sync-btn"
          >
            Jetzt sync
          </button>
        )}
        {hasConflicts && (
          <button
            onClick={(e) => { e.stopPropagation(); dismissAllConflicts(); }}
            className="ml-2 p-0.5 bg-white/20 rounded hover:bg-white/30"
            data-testid="dismiss-conflicts-btn"
          >
            <X className="w-3 h-3" />
          </button>
        )}
      </div>
      {/* Conflict details */}
      {hasConflicts && (
        <div className="bg-red-900/80 text-white px-3 py-2 text-xs space-y-1" data-testid="conflict-details">
          <p className="font-medium">Server hat Vorrang (Last-write-wins):</p>
          {conflicts.slice(0, 3).map((c) => (
            <p key={c.id} className="text-red-200">
              {c.detail || `${c.type || "Aktion"}: Daten wurden auf dem Server geaendert`}
            </p>
          ))}
          {conflicts.length > 3 && (
            <p className="text-red-300">...und {conflicts.length - 3} weitere</p>
          )}
        </div>
      )}
    </div>
  );
}
